/**
 * FINANCIAL CALCULATIONS UTILITY
 * 
 * Shared financial calculation formulas used across both the main application
 * and the investment simulator. All formulas follow Belgian and international
 * real estate standards as documented in financial-definitions.json.
 * 
 * Purpose:
 * - Eliminate code duplication between main app and simulator
 * - Ensure consistent calculations across the application
 * - Provide a single source of truth for financial formulas
 * - Facilitate comprehensive testing of calculation logic
 * 
 * Standards:
 * - Belgian real estate market standards
 * - International real estate investment conventions
 * - Documented in: src/assets/data/financial-definitions.json
 * 
 * @see DOCUMENTATION/SIMULATOR_FORMULA_AUDIT.md
 * @see DOCUMENTATION/KPI_FORMULA_AUDIT.md
 */

/**
 * Financial Calculations Utility Class
 * 
 * Static utility class providing standardized financial calculation formulas.
 * All methods are pure functions with no side effects.
 */
export class FinancialCalculations {
  
  /**
   * Calculate Cap Rate (Capitalization Rate)
   * 
   * Formula: (Net Operating Income ÷ Property Value) × 100
   * 
   * The Cap Rate is the primary metric used in Belgian real estate for property
   * valuation and comparison. It represents the theoretical return if the property
   * were purchased with cash (no financing).
   * 
   * Belgian Standards:
   * - Prime locations (Brussels, Antwerp): 3-5%
   * - Secondary markets: 5-7%
   * - Higher cap rates indicate higher risk or lower property values
   * 
   * @param netOperatingIncome Annual Net Operating Income (NOI) - income after operating expenses but before financing costs
   * @param propertyValue Current market value or purchase price of the property
   * @returns Cap Rate as a percentage (e.g., 4.5 for 4.5%)
   * 
   * @example
   * // Brussels apartment: €49,007 NOI, €1,250,000 value
   * const capRate = FinancialCalculations.calculateCapRate(49007, 1250000);
   * // Returns: 3.92 (3.92% - typical for prime Brussels location)
   * 
   * @reference financial-definitions.json#cap-rate
   */
  static calculateCapRate(netOperatingIncome: number, propertyValue: number): number {
    if (propertyValue <= 0) {
      console.warn('[FinancialCalculations] Cannot calculate Cap Rate: Property value is zero or negative');
      return 0;
    }
    return (netOperatingIncome / propertyValue) * 100;
  }

  /**
   * Calculate Gross Rental Yield
   * 
   * Formula: (Annual Rental Income ÷ Purchase Price) × 100
   * 
   * Gross yield is a simple screening metric that shows the relationship between
   * rental income and property price before considering any expenses. It's useful
   * for quick property comparisons.
   * 
   * Belgian Standards:
   * - City apartments (Brussels, Antwerp, Ghent): 4-6%
   * - Suburban properties: 5-8%
   * - Student housing: 6-9%
   * 
   * @param annualRentalIncome Gross annual rental income (before any deductions)
   * @param purchasePrice Property purchase price (or current market value)
   * @returns Gross yield as a percentage
   * 
   * @example
   * // Brussels apartment: €60,000 annual rent, €1,250,000 purchase price
   * const grossYield = FinancialCalculations.calculateGrossYield(60000, 1250000);
   * // Returns: 4.8 (4.8% - typical for Brussels city apartment)
   * 
   * @reference financial-definitions.json#gross-rental-yield
   */
  static calculateGrossYield(annualRentalIncome: number, purchasePrice: number): number {
    if (purchasePrice <= 0) {
      console.warn('[FinancialCalculations] Cannot calculate Gross Yield: Purchase price is zero or negative');
      return 0;
    }
    return (annualRentalIncome / purchasePrice) * 100;
  }

  /**
   * Calculate Net Rental Yield
   * 
   * Formula: (Net Operating Income ÷ Purchase Price) × 100
   * 
   * Net yield accounts for operating expenses and provides a more accurate picture
   * of property profitability than gross yield. It's typically 2-4% lower than
   * gross yield in Belgian residential properties.
   * 
   * Belgian Standards:
   * - City apartments: 2-4%
   * - Suburban properties: 3-5%
   * - Expense ratio typically 20-40% of gross income
   * 
   * @param netOperatingIncome Annual Net Operating Income (after operating expenses, before financing)
   * @param purchasePrice Property purchase price (or current market value)
   * @returns Net yield as a percentage
   * 
   * @example
   * // Brussels apartment: €49,007 NOI, €1,250,000 purchase price
   * const netYield = FinancialCalculations.calculateNetYield(49007, 1250000);
   * // Returns: 3.92 (3.92% - typical net yield after expenses)
   * 
   * @reference financial-definitions.json#net-rental-yield
   */
  static calculateNetYield(netOperatingIncome: number, purchasePrice: number): number {
    if (purchasePrice <= 0) {
      console.warn('[FinancialCalculations] Cannot calculate Net Yield: Purchase price is zero or negative');
      return 0;
    }
    return (netOperatingIncome / purchasePrice) * 100;
  }

  /**
   * Calculate Cash-on-Cash Return
   * 
   * Formula: (Annual Net Cash Flow ÷ Total Cash Invested) × 100
   * 
   * Cash-on-Cash return measures the actual return on the cash you invested
   * (down payment, closing costs, renovations). Unlike ROI, it focuses only
   * on cash invested, not total property value.
   * 
   * Belgian Standards:
   * - >6%: Good return
   * - >10%: Excellent return
   * - >15%: Outstanding return (rare in Belgian market)
   * 
   * @param annualNetCashFlow Annual net cash flow after all expenses and debt service
   * @param totalCashInvested Total cash invested (down payment + closing costs + renovations)
   * @returns Cash-on-Cash return as a percentage
   * 
   * @example
   * // Investment: €250,000 down payment, €15,000 annual cash flow
   * const cashOnCash = FinancialCalculations.calculateCashOnCashReturn(15000, 250000);
   * // Returns: 6.0 (6% - good return for Belgian market)
   * 
   * @reference financial-definitions.json#cash-on-cash-return
   */
  static calculateCashOnCashReturn(annualNetCashFlow: number, totalCashInvested: number): number {
    if (totalCashInvested <= 0) {
      console.warn('[FinancialCalculations] Cannot calculate Cash-on-Cash Return: Total cash invested is zero or negative');
      return 0;
    }
    return (annualNetCashFlow / totalCashInvested) * 100;
  }

  /**
   * Calculate Debt Service Coverage Ratio (DSCR)
   * 
   * Formula: Net Operating Income ÷ Annual Debt Service
   * 
   * DSCR measures the property's ability to cover debt payments from operating
   * income. Belgian banks typically require DSCR > 1.25 for investment property loans.
   * 
   * Belgian Banking Standards:
   * - <1.0: Cannot cover debt (bank will not lend)
   * - 1.0-1.25: Risky (most banks will not lend)
   * - 1.25-1.5: Acceptable (minimum for most banks)
   * - >1.5: Safe (preferred by lenders)
   * 
   * @param netOperatingIncome Annual Net Operating Income (before debt service)
   * @param annualDebtService Annual debt service (principal + interest payments)
   * @returns DSCR ratio (e.g., 1.35 means NOI is 135% of debt service)
   * 
   * @example
   * // Property: €49,007 NOI, €36,000 annual debt service
   * const dscr = FinancialCalculations.calculateDSCR(49007, 36000);
   * // Returns: 1.36 (136% coverage - acceptable for Belgian banks)
   * 
   * @reference financial-definitions.json#dscr
   */
  static calculateDSCR(netOperatingIncome: number, annualDebtService: number): number {
    if (annualDebtService <= 0) {
      // No (or fully repaid) loan → no debt to service → infinite coverage.
      // This is a normal, expected state for unleveraged properties, not an
      // error, so it is intentionally not logged.
      return Number.POSITIVE_INFINITY;
    }
    return netOperatingIncome / annualDebtService;
  }

  /**
   * Calculate Loan-to-Value Ratio (LTV)
   * 
   * Formula: (Outstanding Loan Balance ÷ Property Value) × 100
   * 
   * LTV measures the loan amount relative to property value. Lower LTV means
   * more equity and less risk. Belgian banks have strict LTV limits for
   * investment properties.
   * 
   * Belgian Banking Standards:
   * - Primary residence: up to 90% LTV
   * - Investment property: typically 80% LTV maximum
   * - >90% LTV: Considered high risk
   * 
   * @param outstandingLoanBalance Current outstanding loan balance
   * @param propertyValue Current market value of the property
   * @returns LTV as a percentage (e.g., 75 for 75% LTV)
   * 
   * @example
   * // Property: €200,000 loan balance, €250,000 current value
   * const ltv = FinancialCalculations.calculateLTV(200000, 250000);
   * // Returns: 80.0 (80% LTV - typical maximum for Belgian investment properties)
   * 
   * @reference financial-definitions.json#ltv-ratio
   */
  static calculateLTV(outstandingLoanBalance: number, propertyValue: number): number {
    if (propertyValue <= 0) {
      console.warn('[FinancialCalculations] Cannot calculate LTV: Property value is zero or negative');
      return 0;
    }
    return (outstandingLoanBalance / propertyValue) * 100;
  }

  /**
   * Calculate Break-Even Occupancy
   * 
   * Formula: ((Operating Expenses + Annual Debt Service) ÷ Gross Potential Income) × 100
   * 
   * Break-even occupancy shows the minimum occupancy rate needed to cover all
   * expenses and debt service. Lower is safer - it means you have more cushion
   * for vacancy periods.
   * 
   * Belgian Standards:
   * - <70%: Very safe (good cushion for vacancy)
   * - 70-80%: Safe (acceptable cushion)
   * - 80-90%: Risky (little room for vacancy)
   * - >90%: Very risky (almost no vacancy tolerance)
   * 
   * @param operatingExpenses Annual operating expenses (excluding debt service)
   * @param annualDebtService Annual debt service (principal + interest)
   * @param grossPotentialIncome Annual gross potential income (100% occupancy)
   * @returns Break-even occupancy as a percentage
   * 
   * @example
   * // Property: €8,000 expenses, €36,000 debt service, €60,000 potential income
   * const breakEven = FinancialCalculations.calculateBreakEvenOccupancy(8000, 36000, 60000);
   * // Returns: 73.3 (73.3% - safe level for Belgian market)
   * 
   * @reference financial-definitions.json#break-even-occupancy
   */
  static calculateBreakEvenOccupancy(
    operatingExpenses: number,
    annualDebtService: number,
    grossPotentialIncome: number
  ): number {
    if (grossPotentialIncome <= 0) {
      console.warn('[FinancialCalculations] Cannot calculate Break-Even Occupancy: Gross potential income is zero or negative');
      return 100; // Cannot break even with no income
    }
    return ((operatingExpenses + annualDebtService) / grossPotentialIncome) * 100;
  }

  /**
   * Calculate Expense Ratio
   * 
   * Formula: (Operating Expenses ÷ Gross Rental Income) × 100
   * 
   * Expense ratio measures the efficiency of property management by showing
   * what percentage of gross income goes to operating expenses. Lower is better,
   * but too low might indicate deferred maintenance.
   * 
   * Belgian Standards:
   * - Residential properties: 20-40%
   * - Commercial properties: 30-50%
   * - <20%: Possibly underestimating or deferring maintenance
   * - >50%: High expenses, investigate efficiency
   * 
   * @param operatingExpenses Annual operating expenses
   * @param grossRentalIncome Gross annual rental income
   * @returns Expense ratio as a percentage
   * 
   * @example
   * // Property: €8,000 expenses, €60,000 gross income
   * const expenseRatio = FinancialCalculations.calculateExpenseRatio(8000, 60000);
   * // Returns: 13.3 (13.3% - low for Belgian residential, may indicate deferred maintenance)
   * 
   * @reference financial-definitions.json#expense-ratio
   */
  static calculateExpenseRatio(operatingExpenses: number, grossRentalIncome: number): number {
    if (grossRentalIncome <= 0) {
      console.warn('[FinancialCalculations] Cannot calculate Expense Ratio: Gross rental income is zero or negative');
      return Number.POSITIVE_INFINITY;
    }
    return (operatingExpenses / grossRentalIncome) * 100;
  }

  /**
   * Calculate Net Operating Income (NOI)
   * 
   * Formula: Gross Rental Income - Operating Expenses
   * 
   * NOI is the fundamental measure of a property's profitability. It represents
   * income from operations before accounting for financing costs and taxes.
   * This is the standard metric used in Belgian real estate for property
   * valuation and comparison.
   * 
   * @param grossRentalIncome Annual gross rental income
   * @param operatingExpenses Annual operating expenses (excluding debt service)
   * @returns Net Operating Income
   * 
   * @example
   * // Property: €60,000 gross income, €10,993 expenses
   * const noi = FinancialCalculations.calculateNOI(60000, 10993);
   * // Returns: 49,007 (€49,007 NOI)
   * 
   * @reference financial-definitions.json#net-operating-income
   */
  static calculateNOI(grossRentalIncome: number, operatingExpenses: number): number {
    return grossRentalIncome - operatingExpenses;
  }

  /**
   * Calculate Effective Gross Income (EGI)
   * 
   * Formula: Gross Rental Income × (1 - Vacancy Rate)
   * 
   * EGI accounts for potential vacancy periods and represents the actual rental
   * income you can realistically expect to collect. Belgian rental market
   * typically assumes 5-8% vacancy rate depending on location and property type.
   * 
   * @param grossRentalIncome Annual gross rental income (100% occupancy)
   * @param vacancyRate Vacancy rate as a percentage (e.g., 5 for 5%)
   * @returns Effective Gross Income
   * 
   * @example
   * // Property: €60,000 gross income, 5% vacancy
   * const egi = FinancialCalculations.calculateEffectiveGrossIncome(60000, 5);
   * // Returns: 57,000 (€57,000 after 5% vacancy)
   * 
   * @reference financial-definitions.json#effective-gross-income
   */
  static calculateEffectiveGrossIncome(grossRentalIncome: number, vacancyRate: number): number {
    return grossRentalIncome * (1 - vacancyRate / 100);
  }
}

